import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import corporateProfileService from '../services/corporateProfileService';
import './ProfileIATAPage.css';

const CorporateProfilesPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [search, setSearch] = useState('');
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadProfiles();
    }, [ppId]); // eslint-disable-line

    const loadProfiles = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await corporateProfileService.list(ppId);
            setProfiles(res.data?.profiles || []);
        } catch (err) {
            setError('Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    const filtered = profiles.filter(p =>
        !search ||
        (p.profile_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.name || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleRemoveProfile = async (id) => {
        if (!window.confirm('Remove this corporate profile?')) return;
        try {
            await corporateProfileService.remove(ppId, id);
            setProfiles(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            setError('Failed to remove profile');
        }
    };

    const ProfileIcon = () => (
        <div className="pi-avatar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="3" width="16" height="18" rx="2" stroke="#7c3aed" strokeWidth="1.6"/>
                <path d="M8 7h8M8 11h8M8 15h5" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to Payment Porter
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Corporate Profile Configuration</h1>
                        <p className="pi-subtitle">Configure Opera Cloud Corporate Profile IDs for reservation filtering</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-porter/${ppId}/corporate-profiles/new`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Profile
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search by Profile ID or Name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {error && <div style={{ color: '#dc2626', padding: '0.5rem 1.5rem', fontSize: '0.875rem' }}>{error}</div>}

            <div className="pi-list-panel">
                {!loading && filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <rect x="4" y="3" width="16" height="18" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M8 7h8M8 11h8M8 15h5" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No profiles match your search' : 'No corporate profiles configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by adding your first corporate profile.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Profile ID</th>
                                <th>Agent Type</th>
                                <th>Name</th>
                                <th>City</th>
                                <th>Country</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div className="pi-profile-cell">
                                            <ProfileIcon />
                                            <span className="pi-id-cell">{p.profile_id}</span>
                                        </div>
                                    </td>
                                    <td>{p.agent_type || <span className="pi-no-value">—</span>}</td>
                                    <td>{p.name || <span className="pi-no-value">—</span>}</td>
                                    <td className="pi-location-cell">{p.city || <span className="pi-no-value">—</span>}</td>
                                    <td className="pi-location-cell">{p.country || <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={p.active ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {p.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="pi-actions">
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${ppId}/corporate-profiles/${p.id}/edit`)}
                                                title="Edit"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={() => handleRemoveProfile(p.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default CorporateProfilesPage;
